import { createTicker } from "@/lib/ticker";

const SAMPLE_MS = 1000 / 20;
const SAMPLING_GAP_MS = 500;
const ACTIVE_THRESHOLD = 0.12;

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
const magnitude = (x = 0, y = 0) => Math.min(1, Math.hypot(x, y));

/**
 * Passive controller research collector. It reads standard Gamepad API reports,
 * never emits synthetic input, and uploads only aggregate session metrics.
 */
export function createControllerResearch(onStatus = () => {}) {
    let running = false;
    let ticker = null;
    let lastSampleAt = 0;
    let startedAt = 0;
    let game = "warzone";
    let previous = null;
    let pendingEvents = [];
    let firstHalfAim = [];
    let secondHalfAim = [];
    let stats = null;
    let preferredIndex = null;
    let lastConnectedAt = 0;
    let lastStatusAt = 0;
    let disconnectedAt = 0;
    let listening = false;

    const emitStatus = (patch = {}) => onStatus({
        connected: Boolean(stats?.connected_samples),
        samples: stats?.connected_samples || 0,
        collecting: running,
        controller: stats?.controller_id || "",
        totalPolls: stats?.samples || 0,
        samplingGaps: stats?.sampling_gaps || 0,
        missedSamples: stats?.missed_samples || 0,
        largestGapMs: Math.round(stats?.largest_gap_ms || 0),
        schedulerPauses: stats?.scheduler_pauses || 0,
        largestSchedulerPauseMs: Math.round(stats?.largest_scheduler_pause_ms || 0),
        ...patch,
    });

    const connectedPads = () => {
        try {
            return Array.from(navigator.getGamepads?.() || []).filter((pad) => Boolean(pad) && pad.connected !== false);
        } catch {
            return [];
        }
    };

    const selectPad = () => {
        const pads = connectedPads();
        return pads.find((pad) => pad.index === preferredIndex) || pads[0] || null;
    };

    const rememberPad = (pad) => {
        if (!pad) return;
        preferredIndex = pad.index;
        lastConnectedAt = Date.now();
        if (stats) {
            stats.controller_id = String(pad.id || "Standard controller").slice(0, 120);
            stats.mapping = String(pad.mapping || "standard").slice(0, 30);
        }
        emitStatus({ connected: true, reason: "gamepad_connected" });
    };

    const onGamepadConnected = (event) => rememberPad(event.gamepad);
    const onGamepadDisconnected = (event) => {
        if (event.gamepad?.index === preferredIndex) preferredIndex = null;
        emitStatus({ connected: Boolean(selectPad()), reason: "gamepad_disconnected" });
    };

    const ensureListeners = () => {
        if (listening) return;
        window.addEventListener("gamepadconnected", onGamepadConnected);
        window.addEventListener("gamepaddisconnected", onGamepadDisconnected);
        listening = true;
    };

    const removeListeners = () => {
        if (!listening) return;
        window.removeEventListener("gamepadconnected", onGamepadConnected);
        window.removeEventListener("gamepaddisconnected", onGamepadDisconnected);
        listening = false;
    };

    const reset = () => {
        stats = {
            samples: 0, active_samples: 0, connected_samples: 0,
            aim_sum: 0, movement_sum: 0, centered_aim_samples: 0,
            aim_reversals: 0, micro_corrections: 0, saturated_aim_samples: 0,
            trigger_presses: 0, button_presses: 0, response_times: [],
            controller_id: "", mapping: "",
            sampling_gaps: 0, missed_samples: 0, hidden_sampling_gaps: 0,
            largest_gap_ms: 0, hidden_samples: 0,
            scheduler_pauses: 0, scheduler_missed_samples: 0,
            largest_scheduler_pause_ms: 0,
        };
        previous = null;
        pendingEvents = [];
        firstHalfAim = [];
        secondHalfAim = [];
        disconnectedAt = 0;
    };

    const activeInput = (aim, move, buttons) =>
        aim > ACTIVE_THRESHOLD || move > ACTIVE_THRESHOLD || buttons.some((button) => button?.pressed);

    const sample = () => {
        if (!running) return;
        const time = performance.now();
        if (time - lastSampleAt < SAMPLE_MS - 2) return;
        const gap = lastSampleAt ? time - lastSampleAt : 0;
        if (gap > SAMPLING_GAP_MS) {
            // Chrome deliberately throttles background tabs. That is a browser
            // scheduling pause, not evidence that the controller disconnected.
            stats.scheduler_pauses += 1;
            stats.scheduler_missed_samples += Math.max(1, Math.round(gap / SAMPLE_MS) - 1);
            stats.largest_scheduler_pause_ms = Math.max(stats.largest_scheduler_pause_ms, gap);
            if (document.visibilityState === "hidden") stats.hidden_sampling_gaps += 1;
        }
        lastSampleAt = time;

        const pad = selectPad();
        stats.samples += 1;
        if (document.visibilityState === "hidden") stats.hidden_samples += 1;
        if (!pad) {
            if (!disconnectedAt) disconnectedAt = time;
            if (time - lastStatusAt >= 1000) {
                lastStatusAt = time;
                emitStatus({
                    connected: false,
                    reason: "waiting_for_gamepad",
                    hint: "Press any controller button once while this tab is visible so Chrome can expose it.",
                });
            }
            return;
        }

        if (disconnectedAt) {
            const disconnectedFor = time - disconnectedAt;
            if (disconnectedFor > SAMPLING_GAP_MS) {
                stats.sampling_gaps += 1;
                stats.missed_samples += Math.max(1, Math.round(disconnectedFor / SAMPLE_MS) - 1);
                stats.largest_gap_ms = Math.max(stats.largest_gap_ms, disconnectedFor);
            }
            disconnectedAt = 0;
        }

        if (pad.index !== preferredIndex) rememberPad(pad);
        stats.connected_samples += 1;
        stats.controller_id = String(pad.id || "Standard controller").slice(0, 120);
        stats.mapping = String(pad.mapping || "standard").slice(0, 30);
        const axes = pad.axes || [];
        const aimX = Number(axes[2] || 0), aimY = Number(axes[3] || 0);
        const move = magnitude(axes[0], axes[1]);
        const aim = magnitude(aimX, aimY);
        const buttons = Array.from(pad.buttons || []);
        const isActive = activeInput(aim, move, buttons);

        if (isActive) stats.active_samples += 1;
        stats.aim_sum += aim;
        stats.movement_sum += move;
        if (aim <= 0.08) stats.centered_aim_samples += 1;
        if (aim >= 0.95) stats.saturated_aim_samples += 1;
        if (aim > 0.08 && aim < 0.35) stats.micro_corrections += 1;

        if (previous) {
            if (Math.abs(aimX) > 0.2 && Math.abs(previous.aimX) > 0.2 && Math.sign(aimX) !== Math.sign(previous.aimX)) stats.aim_reversals += 1;
            if (Math.abs(aimY) > 0.2 && Math.abs(previous.aimY) > 0.2 && Math.sign(aimY) !== Math.sign(previous.aimY)) stats.aim_reversals += 1;
            buttons.forEach((button, index) => {
                if (button?.pressed && !previous.buttons[index]) {
                    stats.button_presses += 1;
                    if (index === 6 || index === 7) stats.trigger_presses += 1;
                }
            });
        }

        const elapsed = Date.now() - startedAt;
        (elapsed < 10 * 60 * 1000 ? firstHalfAim : secondHalfAim).push(aim);
        if (isActive && pendingEvents.length) {
            const now = Date.now();
            const eligible = pendingEvents.filter((eventAt) => now >= eventAt && now - eventAt <= 1500);
            eligible.forEach((eventAt) => stats.response_times.push(now - eventAt));
            pendingEvents = pendingEvents.filter((eventAt) => now - eventAt <= 1500 && !eligible.includes(eventAt));
        }
        previous = { aimX, aimY, buttons: buttons.map((button) => !!button?.pressed) };
        if (stats.connected_samples % 150 === 0) emitStatus({ connected: true, reason: "sampling" });
    };

    const average = (items) => items.length ? items.reduce((sum, value) => sum + value, 0) / items.length : 0;

    return {
        prime() {
            ensureListeners();
            const pad = selectPad();
            if (pad) rememberPad(pad);
            else emitStatus({ connected: false, collecting: running, reason: "waiting_for_gamepad" });
            return Boolean(pad);
        },
        start(nextGame = "warzone") {
            if (running) return;
            reset();
            running = true;
            game = nextGame;
            startedAt = Date.now();
            lastSampleAt = 0;
            lastStatusAt = 0;
            preferredIndex = null;
            ensureListeners();
            rememberPad(selectPad());
            emitStatus({
                connected: Boolean(selectPad()),
                samples: 0,
                collecting: true,
                reason: selectPad() ? "gamepad_detected" : "waiting_for_gamepad",
            });
            ticker = createTicker(SAMPLE_MS, sample);
        },
        noteEvents(events = []) {
            if (!running || !events.length) return;
            pendingEvents.push(Date.now());
            pendingEvents = pendingEvents.slice(-20);
        },
        stop() {
            if (!running) return null;
            running = false;
            ticker?.stop();
            ticker = null;
            const connected = stats.connected_samples > 0;
            const samples = Math.max(1, stats.connected_samples);
            const active = Math.max(1, stats.active_samples);
            const firstAim = average(firstHalfAim);
            const secondAim = average(secondHalfAim);
            const summary = {
                game,
                duration_sec: Math.max(1, Math.round((Date.now() - startedAt) / 1000)),
                connected,
                controller_id: stats.controller_id,
                mapping: stats.mapping,
                sample_count: stats.connected_samples,
                active_ratio: Number((stats.active_samples / samples).toFixed(4)),
                aim_intensity: Number((stats.aim_sum / samples).toFixed(4)),
                movement_intensity: Number((stats.movement_sum / samples).toFixed(4)),
                centering_ratio: Number((stats.centered_aim_samples / samples).toFixed(4)),
                reversal_rate: Number((stats.aim_reversals / active).toFixed(4)),
                micro_correction_ratio: Number((stats.micro_corrections / active).toFixed(4)),
                saturation_ratio: Number((stats.saturated_aim_samples / active).toFixed(4)),
                trigger_presses: stats.trigger_presses,
                button_presses: stats.button_presses,
                reaction_ms: stats.response_times.length ? Math.round(average(stats.response_times)) : null,
                fatigue_delta: Number(clamp(secondAim - firstAim, -1, 1).toFixed(4)),
                sampling_gaps: stats.sampling_gaps,
                missed_samples: stats.missed_samples,
                hidden_sampling_gaps: stats.hidden_sampling_gaps,
                largest_gap_ms: Math.round(stats.largest_gap_ms),
                scheduler_pauses: stats.scheduler_pauses,
                scheduler_missed_samples: stats.scheduler_missed_samples,
                largest_scheduler_pause_ms: Math.round(stats.largest_scheduler_pause_ms),
                hidden_ratio: Number((stats.hidden_samples / Math.max(1, stats.samples)).toFixed(4)),
                last_connected_at: lastConnectedAt ? new Date(lastConnectedAt).toISOString() : null,
            };
            emitStatus({ connected, samples: stats.connected_samples, collecting: false, reason: "stopped" });
            return summary;
        },
        destroy() {
            if (running) this.stop();
            removeListeners();
        },
    };
}
